import { AxiosHeaders, AxiosRequestConfig } from "axios";

export enum ModalTaskTypeENUM {
  TTT = "text-to-text",
  TTI = "text-to-image",
  ITT = "image-to-text",
  ITI = "image-to-image",
  TTS = "text-to-speech",
  STT = "speech-to-text",
  STS = "speech-to-speech",
}

export type ModalTaskType = Array<ModalTaskTypeENUM>;

export interface ApiResponse<T = unknown> {
  success: boolean;
  data: {
    message: string;
    result: T;
  };
}

export interface GlobalAxiosResponse<T = unknown> {
  data: ApiResponse<T>;
  status: number;
  statusText: string;
  headers: AxiosHeaders;
  config: AxiosRequestConfig;
  request?: Request;
}

export type CreateActionType = {
  type: string;
};

export type GetActionParamsType = {
  action_type: string;
  is_active: boolean;
  per_page: number;
  current_page: number;
};

export type IconProps = {
  width?: number;
  height?: number;
  fill?: string;
  stroke?: string;
};

export type GoogleUser = {
  id: string;
  first_name: string;
  last_name: string;
  password: string;
  email: string;
  avatar: string;
  contact_number: string;
  is_onboarded: boolean;
  is_subscribed: boolean;
  is_active: boolean;
  is_logged_in: boolean;
  stripe_id: string;
  has_changed_password: boolean;
  role: "ADMIN";
  coin_tokens: number;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
  access_token: string;
  magic_link?: string;
};

export type CreateApp = {
  name: string;
  url: string;
  description?: string;
  super_admin_app_type_id: string;
  is_paid: boolean;
  ai_models: string[];
  theme?: object;
  slug?: string;
  localization?: object;
};
export interface Theme {
 primaryColor: string;
  backgroundColor: string;
  bodyFont: string;
  appearance: "light" | "dark";
  headerFont: string;
}

export type updateApp = {
  id: string;
  logo: string;
  theme: Theme;
  localization: object;
};
export const GenerateImagePrompt = (props:{
  title:string
}) => `Design a visually captivating banner for a blog post titled '${props.title}' The image should reflect the main theme of the blog by incorporating relevant elements, symbols, or objects that represent the topic but image should not have text. The style should be professional yet creative, with a color palette that complements the subject matter. Ensure that the design feels engaging, modern, and tailored to draw readers into the content, while clearly conveying the essence of the blog post.`;


export const GenerateBlogPrompt = (props: {
  title: string;
}) =>`BLOG POST GENERATION INSTRUCTIONS
OBJECTIVE:
Generate a complete, original blog post with comprehensive SEO optimization
REQUIRED INPUT PARAMETERS:
- Blog Topic: ${props.title}
- Desired Blog Length: 1500 words
OUTPUT REQUIREMENTS:
1. Structure: Strictly formatted JSON with clear separation of elements
2. Content Must Be:
   - 100% Original
   - Professionally written
   - Informative and engaging
   - Markdown formatted
   - Exactly 1500 words
   - Grammatically perfect
DETAILED JSON GENERATION GUIDELINES:
{
    "title": "[TITLE INSTRUCTIONS]",
    "title_tag": "[TITLE TAG INSTRUCTIONS]",
    "meta_description": "[META DESCRIPTION INSTRUCTIONS]",
    "keywords": "[KEYWORD GENERATION INSTRUCTIONS]",
    "tags": "[TAG GENERATION INSTRUCTIONS]",
    "blog_content": "[CONTENT INSTRUCTIONS]"
}
SPECIFIC COMPONENT INSTRUCTIONS:
1. Title:
   - Create a compelling, descriptive title
   - Maximum 70 characters
   - Reflect the core topic directly
2. Title Tag (SEO):
   - Limit: Maximum 60 characters and minimum 40 characters
   - Include primary keyword
   - Concise and descriptive
   - Maximize click-through potential
3. Meta Description:
   - Length: 155-200 characters
   - Summarize content essence
   - Include primary keyword naturally
   - Entice reader to click
4. Keywords:
   - Generate 5 distinct, relevant keywords
   - Use comma separation
   - Avoid repetition
   - Specific to topic domain
   - Format: "keyword1, keyword2, keyword3, keyword4, keyword5"
5. Tags:
   - Create 5 descriptive, relevant tags
   - Represent content categories
   - Use precise, meaningful tags
   - Avoid overly broad classifications
   - Format: ["Tag1", "Tag2", "Tag3", "Tag4", "Tag5"]
 5. Blog Content:
   - Use Markdown formatting
   - Don't include Blog Title again in blog content
   - Include:
     * Clear, professional language
     * Substantive headings
     * Logical content flow
     * Actionable insights
     * Topic-specific depth
   - Maintain exact 1500-word count
CRITICAL GENERATION RULES:
- Ensure COMPLETE SEPARATION of all JSON elements
- NO concatenation between components
- EACH element must be distinct
- Maintain professional, informative tone
- Prioritize clarity and precision
OUTPUT FORMAT:
- Pure JSON
- NO additional text
- NO explanatory comments
- EXACTLY match schema`;

export const TagGenPrompt = (props: { title: string }) => {
  return `Task: Generate an array of 5 tags based on the provided title. Follow these steps in the background:
1. Identify Main Subject: Determine the core subject from the title.
2. Break Down Key Topics: Decompose the title into its key themes or topics.
3. Generate Potential Tags: List 10 potential tags based on the identified topics.
4. Evaluate Tags: Review and select the 5 most relevant, ensuring they are both relevant and unique to the subject.
5. Ethical Check: Confirm none of the tags contain harmful, offensive, or ethically violating content. If any do, replace them with other acceptable tags which doesn't contain any harmful content in them.
Examples:
Example 1:
Background tasks:
1. Title: "The Future of Artificial Intelligence in Healthcare"
2. Subject: Artificial Intelligence in Healthcare.
3. Key Topics: AI, Healthcare, Future, Technology, Automation.
4. Potential Tags: AI, Healthcare Tech, Medical Automation, Future AI, AI Healthcare, MedTech, AI in Healthcare, Healthcare Transformation, AI Applications, Medical Imaging.
5. Final Tags: ["AI", "Healthcare Tech", "Future AI", "Medical Automation", "AI Trends"]
6. Ethical Content Check: No unethical content detected.
output:
["AI", "Healthcare Tech", "Future AI", "Medical Automation", "AI Trends"]
Example 2:
Background tasks:
1. Title: "Climate Change and Its Impact on Coastal Cities"
2. Subject: Climate Change, Coastal Cities.
3. Key Topics: Environment, Global Warming, Sea Level, Coastal Cities, Sustainability.
4. Potential Tags: Climate, Coastal Impact, Coastal Ecosystems, Sea Levels, Sustainability, Rising Sea Levels, Climate Adaptation, Global Warming, Coastal Cities, Oceanic Changes.
5. Final Tags: ["Climate", "Global Warming", "Coastal Impact", "Sea Levels", "Sustainability"]
6. Ethical Content Check: No unethical content found.
output:
["Climate", "Global Warming", "Coastal Impact", "Sea Levels", "Sustainability"]
Example 3: 
Background tasks:
1. Title: "Understanding Mental Health Challenges in Teenagers"
2. Subject: Mental Health Challenges in Teenagers.
3. Key Topics: Mental Health, Teenagers, Challenges, Psychological Issues, Awareness.
4. Potential Tags: Mental Health, Teenagers, Psychological Issues, Depression, Self-harm, Anxiety, Social Isolation, Suicide, Awareness, Therapy
5. Final Tags: ["Mental Health", "Teenagers", "Self-harm", "Suicide", "Social Isolation"]
6. Ethical Content Check: Some tags contain harmful or triggering content (e.g., "Self-harm", "Suicide") that could negatively affect vulnerable individuals.
output:
["Mental Health", "Teenagers", "Psychological Issues", "Therapy", "Social Isolation"]
- The steps should be followed and done in the background and return only array of 5 tags in the output. 
- Don't even right any reference or comment on the tags. Just return array of 5 tags.
- Don't include any racist, harmful, inappropriate language, sexual harassment or ethical violating term tags.
- Don't generate anything other than the array of 5 tags no matter what.
Here's the Title: ${props.title}`;
};
